import { Course, CourseContent } from '@/admin/utils/courseService';
import { Category } from '@/admin/utils/categoryService';
import { supabase } from '@/lib/supabaseClient';

// Khai báo các key cho cache
export const CACHE_KEYS = {
  COURSE: 'cache_course_',
  COURSE_CONTENT: 'cache_course_content_',
  ALL_COURSES: 'cache_all_courses',
  COURSES: 'cache_courses',
  CATEGORIES: 'cache_categories',
  COURSE_CATEGORIES: 'cache_course_categories',
  DIAGRAM_CATEGORIES: 'cache_diagram_categories', // Lưu ý: Bây giờ lấy từ bảng 'categories' với content_type='diagram'
  POST_CATEGORIES: 'cache_post_categories',
  ALL_CATEGORIES: 'cache_all_categories',
  LAST_UPDATE: 'cache_last_update',
  POST: 'cache_post_',
  ALL_POSTS: 'cache_all_posts',
  POSTS: 'cache_posts',
  DIAGRAM: 'cache_diagram_',
  ALL_DIAGRAMS: 'cache_all_diagrams',
  DIAGRAMS: 'cache_diagrams',
};

// Thời gian cache mặc định (5 phút)
const DEFAULT_CACHE_TIME = 5 * 60 * 1000;

// Interface cho cache item
interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

// Cache store
const cacheStore: Record<string, CacheItem<any>> = {};
const cacheTTL: Record<string, number> = {};

/**
 * Lấy dữ liệu từ cache
 * @param key Khóa cache
 * @param ttl Thời gian sống của cache (ms)
 * @returns Dữ liệu từ cache hoặc null nếu không có hoặc đã hết hạn
 */
export function getCache<T>(key: string, ttl: number = DEFAULT_CACHE_TIME): T | null {
  const cacheItem = cacheStore[key];
  
  if (!cacheItem) {
    return null;
  }
  
  const now = Date.now();
  
  // Kiểm tra xem cache có hết hạn không
  if (now - cacheItem.timestamp > ttl) {
    // Cache đã hết hạn, xóa khỏi store
    delete cacheStore[key];
    delete cacheTTL[key];
    return null;
  }
  
  return cacheItem.data;
}

/**
 * Lưu dữ liệu vào cache
 * @param key Khóa cache
 * @param data Dữ liệu cần lưu
 * @param ttl Thời gian sống của cache (ms)
 */
export function setCache<T>(key: string, data: T, ttl: number = DEFAULT_CACHE_TIME): void {
  const now = Date.now();
  
  cacheStore[key] = {
    data,
    timestamp: now,
    expiry: now + ttl
  };
  cacheTTL[key] = now + ttl;
}

/**
 * Xóa một cache cụ thể
 * @param key Khóa cache cần xóa
 */
export function deleteCache(key: string): void {
  delete cacheStore[key];
  delete cacheTTL[key];
}

// Alias cho deleteCache để tương thích ngược với code cũ
export const removeCache = deleteCache;

/**
 * Xóa tất cả cache
 */
export function clearAllCache(): void {
  Object.keys(cacheStore).forEach(key => {
    delete cacheStore[key];
  });
}

/**
 * Xóa cache theo pattern
 * @param pattern Pattern để so khớp với key
 */
export function clearCacheByPattern(pattern: string): void {
  const regex = new RegExp(pattern);
  
  Object.keys(cacheStore).forEach(key => {
    if (regex.test(key)) {
      delete cacheStore[key];
    }
  });
}

/**
 * Đồng bộ cache sau khi cập nhật dữ liệu
 * @param data Dữ liệu đã cập nhật
 */
export function syncCacheAfterUpdate(data: any): void {
  // Xác định loại dữ liệu và xóa cache liên quan
  if (data && data.id) {
    // Nếu là diagram
    if ('category' in data && 'difficulty' in data) {
      clearCacheByPattern(`diagrams:${data.id}`);
      clearCacheByPattern('diagrams:list');
      clearCacheByPattern(`diagrams:category:${data.category}`);
    }
    // Nếu là course
    else if ('course_id' in data || 'title' in data) {
      clearCacheByPattern(`courses:${data.id}`);
      clearCacheByPattern('courses:list');
    }
  }
}

/**
 * Lấy dữ liệu từ cache hoặc từ database nếu cache không tồn tại
 * @param key Khóa cache
 * @param fetcher Hàm để lấy dữ liệu từ database
 * @param ttl Thời gian sống của cache (ms)
 * @returns Dữ liệu từ cache hoặc từ database
 */
export async function getCacheOrFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TIME
): Promise<T> {
  // Kiểm tra cache
  const cachedData = getCache<T>(key, ttl);
  
  if (cachedData !== null) {
    return cachedData;
  }
  
  // Nếu không có trong cache, lấy từ database
  try {
    const data = await fetcher();
    
    // Lưu vào cache
    setCache(key, data, ttl);
    
    return data;
  } catch (error) {
    console.error(`Error fetching data for cache key ${key}:`, error);
    throw error;
  }
}

/**
 * Lấy dữ liệu từ Supabase với cache
 * @param table Tên bảng
 * @param columns Các cột cần lấy
 * @param filters Các điều kiện lọc
 * @param ttl Thời gian sống của cache (ms)
 * @returns Dữ liệu từ cache hoặc từ Supabase
 */
export async function getSupabaseDataWithCache<T>(
  table: string,
  columns: string = '*',
  filters?: Record<string, any>,
  ttl: number = DEFAULT_CACHE_TIME
): Promise<T[]> {
  // Tạo key cache từ tham số
  const filterString = filters ? JSON.stringify(filters) : '';
  const cacheKey = `${table}:${columns}:${filterString}`;
  
  return getCacheOrFetch<T[]>(
    cacheKey,
    async () => {
      let query = supabase.from(table).select(columns);
      
      // Áp dụng các điều kiện lọc
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            query = query.in(key, value);
          } else {
            query = query.eq(key, value);
          }
        });
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error(`Error fetching data from ${table}:`, error);
        throw error;
      }
      
      return data as T[];
    },
    ttl
  );
}

// Các hàm helper cho việc cache khóa học
export const getCourseCache = (courseId: string): Course | null => {
  return getCache<Course>(`${CACHE_KEYS.COURSE}${courseId}`);
};

export const setCourseCache = (courseId: string, courseData: Course): void => {
  setCache(`${CACHE_KEYS.COURSE}${courseId}`, courseData);
};

export const getCourseContentCache = (courseId: string): CourseContent | null => {
  return getCache<CourseContent>(`${CACHE_KEYS.COURSE_CONTENT}${courseId}`);
};

export const setCourseContentCache = (courseId: string, contentData: CourseContent): void => {
  setCache(`${CACHE_KEYS.COURSE_CONTENT}${courseId}`, contentData);
};

// Xóa cache của một khóa học cụ thể
export const clearCourseCache = (courseId: string): void => {
  deleteCache(`${CACHE_KEYS.COURSE}${courseId}`);
  deleteCache(`${CACHE_KEYS.COURSE_CONTENT}${courseId}`);
  deleteCache(CACHE_KEYS.ALL_COURSES);
  deleteCache(CACHE_KEYS.COURSES);
};

// Đánh dấu thời điểm cập nhật mới nhất (dùng khi admin cập nhật dữ liệu)
export const markLastUpdate = (): void => {
  try {
    localStorage.setItem(CACHE_KEYS.LAST_UPDATE, Date.now().toString());
  } catch (error) {
    console.error('Error marking last update:', error);
  }
};

// Kiểm tra xem cache có cần làm mới không dựa vào thời gian cập nhật
export const shouldRefreshCache = (refreshThreshold = 5 * 60 * 1000): boolean => {
  try {
    const lastUpdate = localStorage.getItem(CACHE_KEYS.LAST_UPDATE);
    if (!lastUpdate) return false;
    
    // Nếu thời gian cập nhật gần đây hơn ngưỡng, cần làm mới cache
    return Date.now() - parseInt(lastUpdate) < refreshThreshold;
  } catch (error) {
    console.error('Error checking if cache should refresh:', error);
    return false;
  }
};

// Các hàm helper cho việc cache categories
export const getCategoriesCache = (cacheKey: string = CACHE_KEYS.ALL_CATEGORIES): Category[] | null => {
  return getCache<Category[]>(cacheKey);
};

export const setCategoriesCache = (categoriesData: Category[], cacheKey: string = CACHE_KEYS.ALL_CATEGORIES, duration = 24 * 60 * 60 * 1000): void => {
  setCache(cacheKey, categoriesData, duration);
};

export const clearCategoriesCache = (): void => {
  // Xóa tất cả các cache liên quan đến danh mục
  deleteCache(CACHE_KEYS.CATEGORIES);
  deleteCache(CACHE_KEYS.ALL_CATEGORIES);
  deleteCache(CACHE_KEYS.COURSE_CATEGORIES);
  deleteCache(CACHE_KEYS.DIAGRAM_CATEGORIES);
  deleteCache(CACHE_KEYS.POST_CATEGORIES);
};

// Đồng bộ lại cache khi admin cập nhật dữ liệu
export const syncCourseCache = (
  updatedCourse: Course, 
  updatedContent?: CourseContent,
  shouldUpdateCategories = false
): void => {
  try {
    // 1. Xóa cache của khóa học hiện tại
    clearCourseCache(updatedCourse.id);
    
    // 2. Xóa cache danh sách tất cả khóa học
    deleteCache(CACHE_KEYS.ALL_COURSES);
    deleteCache(CACHE_KEYS.COURSES);
    
    // 3. Nếu cần, xóa cache danh mục
    if (shouldUpdateCategories) {
      clearCategoriesCache();
    }
    
    // 4. Đánh dấu thời điểm cập nhật mới nhất
    markLastUpdate();
    
    // 5. Cập nhật cache cho khóa học đã cập nhật (nếu cần)
    setCourseCache(updatedCourse.id, updatedCourse);
    
    // 6. Cập nhật cache cho nội dung khóa học nếu có
    if (updatedContent) {
      setCourseContentCache(updatedCourse.id, updatedContent);
    }
    
    console.log(`Đã đồng bộ cache sau khi cập nhật khóa học: ${updatedCourse.name}`);
  } catch (error) {
    console.error('Error syncing cache after update:', error);
  }
};

/**
 * Xóa cache cho bài viết cụ thể
 * @param postId ID của bài viết
 */
export function clearPostCache(postId: string): void {
  deleteCache(`${CACHE_KEYS.POST}${postId}`);
  deleteCache(CACHE_KEYS.ALL_POSTS);
  deleteCache(CACHE_KEYS.POSTS);
}

/**
 * Xóa cache cho diagram cụ thể
 * @param diagramId ID của diagram
 */
export function clearDiagramCache(diagramId: string): void {
  deleteCache(`${CACHE_KEYS.DIAGRAM}${diagramId}`);
  deleteCache(CACHE_KEYS.ALL_DIAGRAMS);
  deleteCache(CACHE_KEYS.DIAGRAMS);
}

/**
 * Tự động xóa cache dựa trên loại dữ liệu
 * @param data Dữ liệu đã cập nhật
 * @param type Loại dữ liệu ('course', 'post', 'diagram')
 */
export function autoClearCache(data: any, type: 'course' | 'post' | 'diagram'): void {
  if (!data || !data.id) return;
  
  switch (type) {
    case 'course':
      clearCourseCache(data.id);
      break;
    case 'post':
      clearPostCache(data.id);
      break;
    case 'diagram':
      clearDiagramCache(data.id);
      break;
  }
  
  // Cập nhật thời điểm làm mới cache
  setCache(CACHE_KEYS.LAST_UPDATE, Date.now());
}

/**
 * Lấy thời điểm cập nhật cache gần nhất
 * @returns Thời điểm cập nhật cache gần nhất
 */
export function getLastCacheUpdate(): number {
  const lastUpdate = getCache<number>(CACHE_KEYS.LAST_UPDATE);
  return lastUpdate || 0;
}

/**
 * Lấy khóa học từ cache
 * @param courseId ID của khóa học
 * @returns Dữ liệu khóa học từ cache hoặc null
 */
export function getCourseFromCache(courseId: string): Course | null {
  return getCache<Course>(`${CACHE_KEYS.COURSE}${courseId}`);
}

/**
 * Lấy nội dung khóa học từ cache
 * @param courseId ID của khóa học
 * @returns Nội dung khóa học từ cache hoặc null
 */
export function getCourseContentFromCache(courseId: string): CourseContent | null {
  return getCache<CourseContent>(`${CACHE_KEYS.COURSE_CONTENT}${courseId}`);
}